'use client'

import * as React from 'react'
import { Wand2 } from 'lucide-react'
import { LazyMotion, domAnimation, m, useInView, useReducedMotion } from 'framer-motion'
import { unsplashPlaceholderFor, type PlaceholderCategory } from '@/lib/copy/placeholder-images'
import { EASE, SPRING } from '@/lib/motion'

// MechanismHero, the right-column story device used on every strategic
// marketing page. Inspired by Nosto's "AI Personalization" + "A/B Testing"
// hero overlays. Three layers:
//
//   1. A device frame (browser / laptop) showing a believable product
//      preview, anchored by a real Unsplash photo.
//   2. A "mechanism card", a floating chip that names what's being
//      observed or optimized ("TESTING · Product page CTA"), connected
//      to the live preview by a magic-wand line. This is the line that
//      makes the abstract feature feel tangible.
//   3. Optional persona avatar + variant badge for surfaces where a
//      shopper / variant identity matters (AB testing, agentic, lead
//      capture).
//
// Knobs render below the preview as a "control panel" so the page
// surfaces *how* the feature works without a screenshot tour.
//
// Motion (June 2026): the mechanism now visibly RUNS once on scroll-in,
// the same "AI sees this" language as the AI-tagging frame: a scan line
// sweeps the preview, the mechanism chip blooms in as the verdict, the
// variant badge pops, and the knob bars fill like a control panel coming
// online. Reduced motion resolves straight to the final state.

export type MechanismHeroKnob = {
  label: string
  value: string
}

export type MechanismHeroProps = {
  surfaceLabel: string
  chipEyebrow: string
  chipBody: string
  knobs?: MechanismHeroKnob[]
  imageSeed?: string
  category?: PlaceholderCategory
  persona?: { initials: string; name?: string }
  variantBadge?: string
  footnote?: string
  tone?: 'primary' | 'forest' | 'ocean' | 'sand'
}

const TONES: Record<NonNullable<MechanismHeroProps['tone']>, { card: string; shadow: string; chipBg: string }> = {
  primary: { card: 'from-primary/15 via-transparent to-emerald-200/20',  shadow: 'shadow-primary/10', chipBg: 'bg-primary/95 text-primary-foreground' },
  forest:  { card: 'from-emerald-500/15 via-transparent to-teal-200/20', shadow: 'shadow-emerald-900/10', chipBg: 'bg-emerald-800/95 text-emerald-50' },
  ocean:   { card: 'from-sky-500/15 via-transparent to-cyan-200/20',     shadow: 'shadow-sky-900/10', chipBg: 'bg-sky-800/95 text-sky-50' },
  sand:    { card: 'from-amber-500/15 via-transparent to-rose-200/20',   shadow: 'shadow-amber-900/10', chipBg: 'bg-amber-900/95 text-amber-50' },
}

// Deterministic varied fill widths so the control panel reads as live
// settings rather than three identical bars.
const KNOB_WIDTHS = ['66%', '82%', '54%']

export function MechanismHero(p: MechanismHeroProps) {
  const tone = TONES[p.tone ?? 'primary']
  const img = unsplashPlaceholderFor(p.imageSeed ?? `mech-${p.chipEyebrow}`, {
    category: p.category ?? 'fashion',
    width: 900,
    height: 700,
  })

  const ref = React.useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '0px 0px -15% 0px' })
  const reduce = useReducedMotion()
  const play = inView && !reduce
  const done = inView // reduced motion jumps straight to the final state

  return (
    <LazyMotion features={domAnimation}>
      <div ref={ref} className="relative">
        <div className={`absolute -inset-6 -z-10 rounded-[2.25rem] bg-gradient-to-br ${tone.card} blur-2xl`} />

        <div className={`relative overflow-hidden rounded-3xl border bg-card shadow-2xl ${tone.shadow}`}>
          <div className="flex items-center justify-between border-b bg-secondary/40 px-4 py-2.5">
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-rose-400/60" />
              <span className="h-2 w-2 rounded-full bg-amber-400/60" />
              <span className="h-2 w-2 rounded-full bg-emerald-400/60" />
              <span className="ml-3 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{p.surfaceLabel}</span>
            </div>
            {p.variantBadge && (
              <m.span
                className="flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-[10px] font-black uppercase text-background"
                initial={reduce ? false : { scale: 0, opacity: 0 }}
                animate={done ? { scale: 1, opacity: 1 } : undefined}
                transition={{ ...SPRING, delay: reduce ? 0 : 1.05 }}
              >
                {p.variantBadge}
              </m.span>
            )}
          </div>

          <div className="relative aspect-[5/3.4] overflow-hidden bg-gradient-to-br from-secondary via-secondary to-muted">
            {/* Background-image (not <img>) so a removed/unreachable placeholder
                degrades to the gradient panel instead of a broken-image icon. */}
            <div
              role="presentation"
              style={{ backgroundImage: `url("${img}")` }}
              className="absolute inset-0 bg-cover bg-center"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-black/10 via-transparent to-black/30" />

            {/* Scan sweep: the mechanism reading the page, once. */}
            {play && (
              <m.div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 z-10 h-14"
                style={{
                  background:
                    'linear-gradient(to bottom, transparent, hsl(74 100% 66% / 0.2) 70%, hsl(74 100% 66% / 0.85) 99%, transparent)',
                }}
                initial={{ top: '-16%' }}
                animate={{ top: '108%' }}
                transition={{ duration: 0.95, ease: 'linear', delay: 0.2 }}
              />
            )}

            {p.persona && (
              <m.div
                className="absolute left-4 top-4 flex items-center gap-2"
                initial={reduce ? false : { opacity: 0, x: -10 }}
                animate={done ? { opacity: 1, x: 0 } : undefined}
                transition={{ duration: 0.4, ease: EASE, delay: reduce ? 0 : 0.35 }}
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-card bg-foreground text-[11px] font-bold text-background shadow-lg">
                  {p.persona.initials}
                </span>
                {p.persona.name && (
                  <span className="rounded-full bg-card/90 px-2 py-0.5 text-[10px] font-medium text-foreground backdrop-blur">
                    {p.persona.name}
                  </span>
                )}
              </m.div>
            )}

            {/* magic-wand connecting line + chip: the verdict blooms in after the scan. */}
            <div className="absolute bottom-4 left-4 right-4 flex items-end gap-3">
              <m.div
                className={`relative inline-flex max-w-[78%] items-start gap-2 rounded-2xl px-3 py-2.5 shadow-2xl backdrop-blur ${tone.chipBg}`}
                initial={reduce ? false : { opacity: 0, y: 14, scale: 0.95 }}
                animate={done ? { opacity: 1, y: 0, scale: 1 } : undefined}
                transition={{ ...SPRING, delay: reduce ? 0 : 0.85 }}
              >
                <Wand2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] opacity-80">{p.chipEyebrow}</p>
                  <p className="mt-0.5 text-[12px] leading-snug">{p.chipBody}</p>
                </div>
              </m.div>
            </div>
          </div>

          {p.knobs && p.knobs.length > 0 && (
            <ul className="grid grid-cols-3 divide-x border-t bg-card">
              {p.knobs.slice(0, 3).map((k, i) => (
                <li key={k.label} className="p-3.5">
                  <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{k.label}</p>
                  <p className="mt-1 font-display text-sm font-semibold tabular-nums">{k.value}</p>
                  <div className="mt-2 h-1 overflow-hidden rounded-full bg-secondary">
                    <m.div
                      className="h-full origin-left rounded-full bg-primary/80"
                      style={{ width: KNOB_WIDTHS[i % KNOB_WIDTHS.length] }}
                      initial={reduce ? false : { scaleX: 0 }}
                      animate={done ? { scaleX: 1 } : undefined}
                      transition={{ duration: 0.5, ease: EASE, delay: reduce ? 0 : 1.1 + i * 0.12 }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}

          {p.footnote && (
            <div className="flex items-center justify-between border-t bg-secondary/30 px-4 py-2.5 text-[10px] text-muted-foreground">
              <span>{p.footnote}</span>
              <span className="font-mono">live</span>
            </div>
          )}
        </div>
      </div>
    </LazyMotion>
  )
}
